import AttachmentModal from 'components/molecules/attachment_modal';
import Loader from 'components/atoms/loader';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  destroyBiddingAttachment,
  getBiddingManagementAttachments,
} from 'store/modules/management_store/actions';
import {
  BiddingAttachmentsData,
  ManagementState,
  ManagementTypes,
} from 'store/modules/management_store/types';
import moment from 'moment';
import TextFilter from 'components/atoms/text_filter';
import SelectButton from 'components/atoms/select_button';
import _ from 'underscore';
import { createBiddingManagementDownloadAttachment } from 'services/actions/management_actions';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import createTrack from 'services/actions/tracker_actions';
import { Container } from './styles';
import ImportDocumentsModal from '../import_documents_modal';

interface BiddingClientAttachmentsProps {
  biddingId: number;
  source: 'attachments' | 'documents';
}

const BiddingClientAttachemnts = (props: BiddingClientAttachmentsProps) => {
  const { biddingId, source } = props;
  const dispatch = useDispatch();

  const { REACT_APP_API_ENDPOINT } = process.env;

  const { management_attachments, request_management_attachments } = useSelector<
    ApplicationState,
    ManagementState
  >((state) => state.session.management);

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [filteredAttachmentsList, setFilteredAttachmentsList] = useState<
    Array<BiddingAttachmentsData>
  >([]);
  const [textFilterValue, setTextFilterValue] = useState<string>('');
  const [attachmentModalOpened, setAttachmentModalOpened] = useState<boolean>(false);
  const [importDocumentsModalOpened, setImportDocumentsModalOpened] = useState<boolean>(false);
  const [referer, setReferer] = useState<string>('');
  const [selectedAttachments, setSelectedAttachments] = useState<Array<number>>([]);
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getBiddingManagementAttachments(biddingId, companyId(profile)));
    dispatch({ type: ManagementTypes.UPDATE_STORE_FIELDS });
  }, []);

  useEffect(() => {
    if (source === 'attachments')
      setFilteredAttachmentsList(
        management_attachments[biddingId]?.filter(
          (attachment) => attachment.referer !== 'documents',
        ) || [],
      );

    if (source === 'documents')
      setFilteredAttachmentsList(
        management_attachments[biddingId]?.filter(
          (attachment) => attachment.referer === 'documents',
        ) || [],
      );
  }, [management_attachments]);

  useEffect(() => {
    if (textFilterValue === '')
      return setFilteredAttachmentsList(management_attachments[biddingId] || []);

    return setFilteredAttachmentsList(
      (management_attachments[biddingId] || []).filter((attachment) =>
        attachment.filename.toLowerCase().includes(textFilterValue.toLowerCase()),
      ),
    );
  }, [textFilterValue]);

  const toggleSelectedAttachments = (id: number, updateAll = false) => {
    if (updateAll) {
      if (wholePageSelected) {
        const attachmentList = filteredAttachmentsList.map((attachment) => attachment.id);
        setSelectedAttachments(
          selectedAttachments.filter(
            (selectedAttachment) => !attachmentList.includes(selectedAttachment),
          ),
        );
      }
      if (!wholePageSelected)
        setSelectedAttachments(
          _.uniq([
            ...selectedAttachments,
            ...(filteredAttachmentsList.map((attachment) => attachment.id) || []),
          ]),
        );
    }
    if (!updateAll) {
      if (selectedAttachments.includes(id))
        setSelectedAttachments(
          selectedAttachments.filter((selectedAttachment) => selectedAttachment !== id),
        );
      if (!selectedAttachments.includes(id)) setSelectedAttachments([...selectedAttachments, id]);
    }
  };

  useEffect(() => {
    if (filteredAttachmentsList.length === 0) return;

    if (selectedAttachments.length === filteredAttachmentsList.length) {
      setWholePageSelected(true);
    } else {
      setWholePageSelected(false);
    }
  }, [selectedAttachments]);

  return (
    <Container>
      {request_management_attachments.get_management_attachments.loading && <Loader />}
      {request_management_attachments.get_management_attachments.loaded && (
        <>
          <h5 className="p-2 bg-secondary w-100 mb-3 text-white">
            {source === 'attachments' ? 'Lista de Anexos' : 'Documentos de Habilitação'}
          </h5>
          <div className="row">
            <div className="col-md-6">
              <TextFilter
                textFilterValue={textFilterValue}
                setTextFilterValue={setTextFilterValue}
              />
            </div>
            <div className="col-md-6 text-md-right text-center">
              {source === 'attachments' && (
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={() => {
                    setAttachmentModalOpened(true);
                    setReferer('');
                  }}
                >
                  Adicionar Anexo
                </button>
              )}
              {source === 'documents' && (
                <>
                  <button
                    className="btn btn-primary btn-sm mr-2"
                    type="button"
                    disabled={selectedAttachments.length === 0}
                    onClick={() => {
                      createBiddingManagementDownloadAttachment(selectedAttachments);
                      createTrack('Gestão de Licitações - Efetuou o download de alguns documentos');
                    }}
                  >
                    Download
                  </button>
                  <button
                    className="btn btn-primary btn-sm mr-2"
                    type="button"
                    onClick={() => setImportDocumentsModalOpened(true)}
                  >
                    Importar documento
                  </button>
                  <button
                    className="btn btn-primary btn-sm mt-md-0 mt-2"
                    type="button"
                    onClick={() => {
                      setAttachmentModalOpened(true);
                      setReferer('documents');
                    }}
                  >
                    Adicionar documento
                  </button>
                </>
              )}
            </div>
          </div>

          {filteredAttachmentsList.length === 0 && (
            <div className="row justify-content-center">
              <div className="col-8 mt-3 alert alert-secondary m-0 text-center" role="alert">
                <p className="mb-0">Essa licitação não possui anexos.</p>
              </div>
            </div>
          )}

          {filteredAttachmentsList.length > 0 && (
            <>
              <div className="row attachments-header align-items-center py-2 mt-3 mx-0 d-md-flex d-none">
                {source === 'documents' && (
                  <div className="col-md-1">
                    <SelectButton
                      id={0}
                      isChecked={wholePageSelected}
                      text=""
                      toggleSelected={toggleSelectedAttachments}
                    />
                  </div>
                )}
                <div className="col-md-4">Arquivo</div>
                {/* <div className="col-md">Descrição</div> */}
                <div className="col-md">Origem</div>
                <div className="col-md">Anexado em</div>
                <div className="col-md-1" />
                <div className="col-md-1" />
              </div>
              {filteredAttachmentsList.map((attachment) => (
                <div
                  className="row border-bottom align-items-center py-2 mx-0"
                  title={attachment?.description}
                >
                  {source === 'documents' && (
                    <div className="col-md-1">
                      <SelectButton
                        id={attachment.id}
                        text=""
                        isChecked={selectedAttachments.includes(attachment.id)}
                        toggleSelected={toggleSelectedAttachments}
                      />
                    </div>
                  )}
                  <div className="col-md-4">
                    <span className="d-md-none d-inline-block font-weight-bold mr-1">Arquivo:</span>
                    <span>{attachment.filename}</span>
                  </div>
                  {/* <div className="col-md">
                    <span className="d-md-none d-inline-block font-weight-bold mr-1">
                      Descrição:
                    </span>
                    <span className="text-truncate d-inline-block long-text">
                      {attachment.description}
                    </span>
                  </div> */}
                  <div className="col-md">
                    <span className="d-md-none d-inline-block font-weight-bold mr-1">Origem:</span>
                    <span className="badge badge-warning">{attachment.referer_name}</span>
                  </div>
                  <div className="col-md">
                    <span className="d-md-none d-inline-block font-weight-bold mr-1">
                      Anexado em:
                    </span>
                    {moment(attachment.created_at).format('L')}
                  </div>
                  <div className="col-md-2">
                    <div className="d-flex">
                      <a
                        className="btn btn-link btn-sm mr-1 p-0 d-flex align-items-center"
                        href={`${REACT_APP_API_ENDPOINT}/gerenciar_licitacoes/anexos/${attachment.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span className="material-icons">description</span>
                        <span className="d-md-none d-inline-block ml-1">Download</span>
                      </a>
                      <button
                        type="button"
                        className="btn btn-link p-0 btn-sm d-flex align-items-center text-danger"
                        onClick={() => dispatch(destroyBiddingAttachment(attachment.id, biddingId))}
                      >
                        <i className="material-icons">delete</i>
                        <span className="d-md-none d-inline-block ml-1">Excluir</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
      {attachmentModalOpened && (
        <AttachmentModal
          attachmentModalOpened={attachmentModalOpened}
          referer={referer}
          setAttachmentModalOpened={setAttachmentModalOpened}
          biddingId={biddingId}
        />
      )}
      {importDocumentsModalOpened && (
        <ImportDocumentsModal
          importDocumentsModalOpened={importDocumentsModalOpened}
          setImportDocumentsModalOpened={setImportDocumentsModalOpened}
          biddingId={biddingId}
        />
      )}
    </Container>
  );
};

export default BiddingClientAttachemnts;
